import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Search, Tag } from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import { getBrandsDirectory } from "../lib/api";
import StoreSwitcher from "../components/StoreSwitcher";

export default function BrandsDirectory() {
    const { storeSlug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(() => {
            if (!cancelled) {
                setData(null);
                setErr(null);
            }
        });
        getBrandsDirectory(storeSlug)
            .then((d) => { if (!cancelled) Promise.resolve().then(() => setData(d)); })
            .catch(() => { if (!cancelled) Promise.resolve().then(() => setErr("error")); });
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    const filtered = useMemo(() => {
        if (!data?.brands) return [];
        const needle = q.trim().toLowerCase();
        if (!needle) return data.brands;
        return data.brands.filter((b) => b.name.toLowerCase().includes(needle));
    }, [data, q]);

    if (err) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                <SEOHead
                    title={`Brands · ${storeSlug}`}
                    description="The ZenZest live brand menu is temporarily unavailable. Please refresh in a moment."
                    canonical={`https://weeddispensarynearme.com/brands/${storeSlug}`}
                />
                Live menu temporarily unavailable. Please try again.
            </div>
        );
    }
    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                <SEOHead
                    title={`Brands · ${storeSlug}`}
                    description="Loading the latest cannabis brands available at this ZenZest location."
                    canonical={`https://weeddispensarynearme.com/brands/${storeSlug}`}
                />
                Loading brands…
            </div>
        );
    }

    const { brands, store_name: storeName } = data;
    const canonical = `https://weeddispensarynearme.com/brands/${storeSlug}`;
    const totalProducts = brands.reduce((s, b) => s + b.productCount, 0);

    const schema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Cannabis brands at ${storeName || "ZenZest"}`,
        url: canonical,
        mainEntity: {
            "@type": "ItemList",
            numberOfItems: brands.length,
            itemListElement: brands.slice(0, 50).map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://weeddispensarynearme.com/menu/${storeSlug}/brand/${b.slug}`,
                name: b.name,
            })),
        },
    };

    return (
        <>
            <SEOHead
                title={`Shop by Brand — ${storeName || "ZenZest"} | ${brands.length} cannabis brands`}
                description={`Browse ${brands.length} cannabis brands carried at ${storeName || "ZenZest"} — ${totalProducts} live products. Find flower, edibles, vapes and concentrates by your favorite brand.`}
                canonical={canonical}
            />
            <SchemaJsonLd schema={schema} />

            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-14 sm:py-18">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <Link
                            to={`/menu/${storeSlug}`}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] opacity-80 hover:opacity-100 mr-4"
                        >
                            <ArrowLeft size={12} /> Back to menu
                        </Link>
                        <StoreSwitcher currentSlug={storeSlug} tone="light" />
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.3em] opacity-80 mb-3 inline-flex items-center gap-2">
                        <Tag size={12} /> Shop by Brand
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight max-w-4xl">
                        {brands.length} brands · {totalProducts.toLocaleString()} products live at{" "}
                        {storeName || "ZenZest"}
                    </h1>

                    <div className="relative max-w-md mt-8">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60"
                        />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="brand-directory-search"
                            placeholder="Search brands…"
                            className="w-full bg-zen-cream/10 border border-zen-cream/15 pl-10 pr-4 py-3 text-sm text-zen-cream placeholder:text-zen-cream/50 focus:outline-none focus:border-zen-cream/40"
                        />
                    </div>
                </div>
            </section>

            <section className="bg-zen-bg">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
                    {filtered.length === 0 ? (
                        <p className="text-center text-zen-ink-soft py-12">
                            No brands match &ldquo;{q}&rdquo;.
                        </p>
                    ) : (
                        <div
                            data-testid="brand-directory-grid"
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
                        >
                            {filtered.map((b) => (
                                <Link
                                    key={b.slug}
                                    to={`/menu/${storeSlug}/brand/${b.slug}`}
                                    data-testid={`brand-card-${b.slug}`}
                                    className="group bg-white border border-zen-ink/10 p-5 flex flex-col items-center text-center hover:border-zen-olive transition-colors"
                                >
                                    <div className="aspect-square w-full bg-zen-surface mb-4 flex items-center justify-center p-4 overflow-hidden">
                                        {b.imageUrl ? (
                                            <img
                                                src={b.imageUrl}
                                                alt={`${b.name} logo`}
                                                loading="lazy"
                                                className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
                                            />
                                        ) : (
                                            <span className="font-serif text-3xl text-zen-ink-soft font-light">
                                                {b.name.slice(0, 1).toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-serif text-sm sm:text-base text-zen-ink font-light leading-tight mb-1 line-clamp-2">
                                        {b.name}
                                    </h3>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-zen-olive">
                                        {b.productCount} {b.productCount === 1 ? "product" : "products"}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
